import { Decimal, type CreditLineSummary } from '@paguz/contracts';
import { Badge } from '@paguz/ui';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { PrivateMoney, setAmountsHidden, useAmountsHidden } from '../../components/money.tsx';

/** Porcentaje de uso del cupo, solo para dibujar la barra (no es una cifra financiera). */
function usageWidth(line: CreditLineSummary): string {
  const approved = new Decimal(line.approvedLimit.amount);
  if (approved.lte(0)) return '0%';
  const used = new Decimal(line.approvedLimit.amount).minus(line.availableLimit.amount);
  return `${Decimal.min(100, Decimal.max(0, used.div(approved).times(100))).toFixed(1)}%`;
}

/** Tarjeta de cupo: disponible protagonista, aprobado y utilizado; permite ocultar montos. */
export function CreditLineCard({ line }: { line: CreditLineSummary }) {
  const hidden = useAmountsHidden();
  const delinquent = line.status === 'DELINQUENT' || line.dpd > 0;
  const blocked = line.status === 'BLOCKED';

  return (
    <section
      aria-label="Tu cupo de crédito"
      className="relative overflow-hidden rounded-2xl bg-inverse p-5 text-inverse-foreground shadow-md"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-16 -right-16 size-48 rounded-full bg-highlight/40 blur-2xl"
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <p className="text-sm text-inverse-muted-foreground">Cupo disponible</p>
          <PrivateMoney
            value={line.availableLimit.amount}
            size="xl"
            tone="inverse"
            className="font-display text-4xl leading-none font-semibold"
          />
        </div>
        <button
          type="button"
          onClick={() => {
            setAmountsHidden(!hidden);
          }}
          aria-pressed={hidden}
          aria-label={hidden ? 'Mostrar montos' : 'Ocultar montos'}
          className="-mt-2 -mr-2 flex size-11 items-center justify-center rounded-full text-inverse-foreground hover:bg-white/10"
        >
          {hidden ? <EyeIcon aria-hidden /> : <EyeOffIcon aria-hidden />}
        </button>
      </div>

      <div
        className="relative mt-4 h-2 w-full overflow-hidden rounded-full bg-white/20"
        role="presentation"
      >
        <div
          className="h-full rounded-full bg-inverse-accent"
          style={{ width: usageWidth(line) }}
        />
      </div>

      <dl className="relative mt-3 grid grid-cols-2 gap-2 text-sm">
        <div>
          <dt className="text-inverse-muted-foreground">Utilizado</dt>
          <dd>
            <PrivateMoney value={line.usedPrincipal.amount} size="sm" tone="inverse" />
          </dd>
        </div>
        <div className="text-right">
          <dt className="text-inverse-muted-foreground">Cupo aprobado</dt>
          <dd>
            <PrivateMoney value={line.approvedLimit.amount} size="sm" tone="inverse" />
          </dd>
        </div>
      </dl>

      {(delinquent || blocked) && (
        <div className="relative mt-4 flex flex-wrap items-center gap-2">
          <Badge variant="danger">
            {blocked ? 'Cupo bloqueado' : `En mora · ${String(line.dpd)} días`}
          </Badge>
          <span className="text-sm text-inverse-muted-foreground">
            {blocked ? 'Comunícate con tu banco.' : 'Ponte al día para seguir comprando.'}
          </span>
        </div>
      )}
    </section>
  );
}
